import { z } from 'zod';
import { getAddress } from 'viem';
import type { QueryClient } from '@tanstack/react-query';
import { pollUntil } from '@/modules/poll-until';
import { queryKeys } from '@/modules/query-keys';
import { fetchIssuerByAddress } from '@/modules/sanr-api';
import { isSameAddress } from '@/modules/is-same-address';
import type {
  SellSubscriptionDetails,
  StakeDepositDetails,
  StakeWithdrawDetails,
  TxKind,
} from '@/shared/utils/txKinds';

// wagmi caches single reads under ['readContract', …] and useReadContracts
// batches under ['readContracts', …] — two SEPARATE key families
// (array-prefix matching never crosses them). Every on-chain mutation
// invalidates both so batched reads (e.g. the Stakes tab's canWithdraw
// batch) refresh too.
const CONTRACT_READ_ROOTS = [['readContract'], ['readContracts']] as const;

// Every query root that reads active_stake_on_user or stake rows.
const STAKE_QUERY_ROOTS = [
  queryKeys.issuersAll(),
  queryKeys.myPositionAll(),
  queryKeys.arenaIssuerAll(),
  queryKeys.arenaStrategyIssuers(),
  queryKeys.arenaStakersAll(),
  queryKeys.arenaPortfolioStakesAll(),
  queryKeys.arenaIssuersByAddressesAll(),
  queryKeys.arenaStakeUsersAll(),
  queryKeys.arenaStakeUserAll(),
] as const;

// Which query roots each tx kind refreshes, split by WHEN they are safe to
// refetch: `mined` roots hold on-chain state and refetch as soon as the
// receipt lands; `indexed` roots hold indexer-derived API state and refetch
// only after the kind's indexer poll confirms — refetching them earlier
// returns the pre-tx value.
const KIND_QUERY_ROOTS: Record<
  TxKind,
  {
    mined: readonly (readonly string[])[];
    indexed: readonly (readonly string[])[];
  }
> = {
  sell_subscription: { mined: [], indexed: [] },
  claim_san: {
    mined: [['withdraw-balance'], ...CONTRACT_READ_ROOTS],
    indexed: [],
  },
  claim_points: {
    mined: [['withdraw-balance'], ...CONTRACT_READ_ROOTS],
    indexed: [],
  },
  claim_legacy_san: {
    mined: [['withdraw-balance'], ...CONTRACT_READ_ROOTS],
    indexed: [],
  },
  withdraw_zk_fees: {
    mined: [['withdraw-balance'], ...CONTRACT_READ_ROOTS],
    indexed: [],
  },
  stake_approve: { mined: CONTRACT_READ_ROOTS, indexed: [] },
  stake_deposit: { mined: CONTRACT_READ_ROOTS, indexed: STAKE_QUERY_ROOTS },
  stake_withdraw: { mined: CONTRACT_READ_ROOTS, indexed: STAKE_QUERY_ROOTS },
};

export async function invalidateKindQueries(
  queryClient: QueryClient,
  kind: TxKind,
  phase: 'mined' | 'indexed',
): Promise<void> {
  for (const root of KIND_QUERY_ROOTS[kind][phase]) {
    await queryClient.invalidateQueries({ queryKey: [...root] });
  }
}

// Transport errors and non-2xx responses count as a failed poll attempt — a
// rethrow would abort the whole post-mine cascade on one transient blip.
// Resolves undefined on failure; neither arena endpoint answers a body-less
// 200, so the callers' schemas reject it like any malformed body.
async function fetchJsonOrUndefined(url: string): Promise<unknown> {
  const res = await fetch(url).catch(() => undefined);
  if (res?.ok !== true) return undefined;
  return res.json().catch(() => undefined);
}

// A malformed body counts as a failed attempt, like transport errors.
function pollArenaList<S extends z.ZodType>(
  url: string,
  rowSchema: S,
  done: (rows: z.output<S>[]) => boolean,
  maxAttempts: number,
): Promise<boolean> {
  return pollUntil(
    async () => {
      const body = z
        .looseObject({ data: z.array(rowSchema) })
        .safeParse(await fetchJsonOrUndefined(url));
      if (!body.success) return false;
      return done(body.data.data);
    },
    { intervalMs: 1001, maxAttempts },
  );
}

// Polls the AA indexer until the provider's active_stake_on_user reflects the
// mined tx: up for deposits, down for withdraws. Bounded — on timeout the
// caller's invalidations still run (against possibly-unindexed data; the
// queries' own refetch cycles pick the indexed value up later).
export function waitForStakeIndexed(
  details: StakeDepositDetails | StakeWithdrawDetails,
): Promise<boolean> {
  const prior = Number(details.priorActiveStake);
  const filter = encodeURIComponent(
    JSON.stringify({ address: { equals: details.kolAddress } }),
  );
  const url = `https://api-arena.santiment.net/v1/issuers?limit=1&filter=${filter}`;
  return pollArenaList(
    url,
    z.looseObject({ active_stake_on_user: z.string() }),
    (rows) => {
      const row = rows[0];
      if (row === undefined) return false;
      // The indexer serves '' until it has computed the field — a
      // not-yet-known value, never a zero stake.
      if (row.active_stake_on_user === '') return false;
      const current = Number(row.active_stake_on_user);
      return details.kind === 'stake_deposit' ?
          current > prior
        : current < prior;
    },
    60,
  );
}

// Punished stakes never move active_stake_on_user, so their withdraws poll
// the row set instead: once the indexer records the withdrawal, the deposit
// drops out of the `withdrawn: false` listing the Stakes tab reads. Shorter
// cap — this is only a freshness grace before the indexed-phase
// invalidations; on timeout the refetch still runs.
export function waitForStakeWithdrawn(
  details: StakeWithdrawDetails,
): Promise<boolean> {
  const filter = encodeURIComponent(
    JSON.stringify({ provider_address: details.kolAddress, withdrawn: false }),
  );
  const url = `https://api-arena.santiment.net/v1/stakes?limit=500&filter=${filter}`;
  return pollArenaList(
    url,
    z.looseObject({ stake_id: z.number() }),
    (rows) => !rows.some((s) => s.stake_id === details.stakeId),
    20,
  );
}

// Exit is directional (balance dropped AT LEAST by the sold amount) and
// bounded: an exact-equality check wedges when a concurrent trade moves
// the balance by a different delta or the click-time iOwnAmount snapshot
// is stale, and the persisted tx would re-enter the loop on every reload.
export function waitForSubscriptionSold(
  details: SellSubscriptionDetails,
  profileAddress: string | null,
): Promise<boolean> {
  if (profileAddress == null) return Promise.resolve(false);
  const issuer = getAddress(profileAddress);
  const target = details.iOwnAmount - Number(details.inputValue);
  return pollUntil(
    async () => {
      let row;
      // A thrown read (502 during a backend redeploy, network blip) is a
      // failed attempt, not a cascade abort.
      try {
        row = await fetchIssuerByAddress(issuer, undefined);
      } catch {
        return false;
      }
      if (row === null) return false;
      // A subscription row that vanished entirely is a balance of zero —
      // every key sold.
      const sharesBalanceAfter =
        row.subscriptions.find((i) =>
          isSameAddress(i.issuer.address, details.issuerAddress),
        )?.sharesBalance ?? 0;
      return sharesBalanceAfter <= target;
    },
    { intervalMs: 1001, maxAttempts: 60 },
  );
}
